import { useEffect, useRef, useState } from "react";

/**
 * Holds a loading flag on screen long enough to actually be seen.
 *
 * Tying a spinner straight to a fetch looks fine against a remote API, but the
 * same call answers in ~1ms against a local backend — the spinner mounts and
 * unmounts inside a single frame, so it either flickers or never paints at
 * all. This keeps it up for `minMs` once shown, which costs nothing on a slow
 * connection (the fetch is still the long pole) and makes a fast one legible
 * instead of a flash.
 */
export function useVisibleLoading(loading: boolean, minMs = 500): boolean {
  const [visible, setVisible] = useState(loading);
  const shownAt = useRef<number>(Date.now());

  useEffect(() => {
    if (loading) {
      shownAt.current = Date.now();
      setVisible(true);
      return;
    }

    const remaining = minMs - (Date.now() - shownAt.current);
    if (remaining <= 0) {
      setVisible(false);
      return;
    }

    const timer = setTimeout(() => setVisible(false), remaining);
    return () => clearTimeout(timer);
  }, [loading, minMs]);

  return visible;
}
